// Hook for hash mode state and handlers
// This encapsulates all the logic for the "Sign Image" tab

import { useState, useRef, useCallback } from "react";
import type { HashResult } from "../types";
import { hashImage } from "../api/client";

export interface UseHashModeReturn {
  // State
  image: File | null;
  preview: string | null;
  hashes: HashResult | null;
  loading: boolean;
  loadingStatus: string;
  error: string | null;

  // Actions
  handleFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleDrop: (e: React.DragEvent) => void;
  handleHashImage: () => Promise<void>;
  reset: () => void;

  // Refs
  fileInputRef: React.RefObject<HTMLInputElement | null>;
}

export function useHashMode(): UseHashModeReturn {
  const [image, setImage] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [hashes, setHashes] = useState<HashResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const reset = useCallback(() => {
    setImage(null);
    setPreview(null);
    setHashes(null);
    setError(null);
    setLoadingStatus("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        setImage(file);
        setPreview(URL.createObjectURL(file));
        setHashes(null);
        setError(null);
      }
      // Reset input value to allow selecting the same file again
      e.target.value = "";
    },
    []
  );

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) {
      setImage(file);
      setPreview(URL.createObjectURL(file));
      setHashes(null);
      setError(null);
    }
  }, []);

  const handleHashImage = useCallback(async () => {
    if (!image) return;

    setLoading(true);
    setError(null);
    setLoadingStatus("Uploading image...");

    try {
      setLoadingStatus("Generating perceptual hashes...");
      const result = await hashImage(await image.arrayBuffer(), image.type);
      setLoadingStatus("Processing results...");
      setHashes(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
      setLoadingStatus("");
    }
  }, [image]);

  return {
    image,
    preview,
    hashes,
    loading,
    loadingStatus,
    error,
    handleFileSelect,
    handleDrop,
    handleHashImage,
    reset,
    fileInputRef,
  };
}
